import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {UploadService} from '@shared/services/upload.service';
import {ContentLocale, ToastService} from '@paperlessio/sdk/api/util';
import {BLOCKS_CONFIG, BlocksConfig} from '@blocks/blocks.config';
import {SessionStore} from 'projects/submission/src/app/session/session.store';
import {BlockComponent} from '@blocks/block/block.component';
import {Blob, blobIdFromSignedId, BlockType, FileUploadInput, SecureFileUploadInput, SubmitEventValue} from '@paperlessio/sdk/api/models';
import {FormControl, NgForm, ValidationErrors} from '@angular/forms';
import {SessionControllerService} from 'projects/submission/src/app/session/session-controller.service';
import {LocalizationService} from '@shared/i18n/localization.service';
import * as Sentry from '@sentry/angular-ivy';
import {Subscription} from 'rxjs';
import {LiveAnnouncer} from '@angular/cdk/a11y';
import {TranslocoService} from '@ngneat/transloco';
import {PdfPartComponent} from '@blocks/pdf-part/pdf-part.component';

@Component({
  selector: 'app-file-upload-input',
  templateUrl: './file-upload-input.component.html',
  styleUrls: ['./file-upload-input.component.scss']
})
export class FileUploadInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() block: FileUploadInput | SecureFileUploadInput;
  @Input() slug: string;
  @Input() data: string[];
  @Output() dataChange: EventEmitter<string[]> = new EventEmitter<string[]>();

  private localizationService = inject(LocalizationService);
  private translocoService = inject(TranslocoService);
  private uploadService = inject(UploadService);
  private toast = inject(ToastService);
  private cdr = inject(ChangeDetectorRef);
  private form = inject(NgForm);
  private liveAnnouncer = inject(LiveAnnouncer);
  private sessionController = inject(SessionControllerService, {optional: true});
  private sessionStore = inject(SessionStore, {optional: true});
  private blockComponent = inject(BlockComponent, {optional: true});
  public blocksConfig: BlocksConfig = inject(BLOCKS_CONFIG);
  private pdfPartComponent = inject(PdfPartComponent, {optional: true});

  public domId = '';
  public formControl: FormControl<string[] | null>;
  public ineligible = false;
  public submitEventValue: SubmitEventValue;
  public blobs: Blob[] = [];

  private errorTranslationKey;
  private subs = new Subscription();

  // Keep this in sync with the list in the SecureFileCreationProcess Server class
  public sfusSupportedMimeTypes = [
    '.pdf',
    '.txt',
    'text/plain',
    'application/octet-stream',
    'application/pdf',
    'image/gif',
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/bmp',
    'image/tiff',
    'image/heic',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.text'
  ];

  @HostListener('window:beforeunload', []) canDeactivate() {
    return !this.uploadService?.isUploading();
  }

  get isOverlay(): boolean {
    return !!this.pdfPartComponent;
  }

  get isSecureFileUpload() {
    return this.block.type === BlockType.SecureFileUploadInput;
  }

  ngOnInit() {
    this.errorTranslationKey = 'blocks.' + this.block.type;
    this.formControl = new FormControl<string[] | null>(this.data, () => this._valid());

    if (this.sessionStore) {
      this.submitEventValue = this.sessionStore.getAggregatedSubmissionData(this.slug);
    }

    if (this.blocksConfig.submittable) {
      this.domId = 'r' + (this.blocksConfig.editable ? 'e' : '') + (this.block.settings.editable ? 'q' : '');
      this.ineligible = this.sessionController && !this.sessionController.isParticipantAnOwnerOf(this.block.id);
    }

    // we have to disable manually to prevent invalid coloring
    if (this.ineligible) {
      this.formControl.disable();
    }

    this.form.form.addControl(this.slug, this.formControl);

    if ((!this.blocksConfig.submittable && !this.blocksConfig.editable) || this.blocksConfig.submittable) {
      if (this.submitEventValue) {
        this.blobs = this.submitEventValue.files;
      }
    }

    // redraw if one of our blobs has an upload progress
    this.subs.add(this.uploadService.progress.subscribe(blob => {
      if (this.blobs?.indexOf(blob) > -1) {
        this.cdr.markForCheck();
      }
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if slug has changed, we need to update the form control
    if (changes.slug && this.formControl) {
      this.form.form.removeControl(changes.slug.previousValue);
      this.form.form.addControl(changes.slug.currentValue, this.formControl);
    }
  }

  ngOnDestroy() {
    this.form.form.removeControl(this.slug);
    this.subs.unsubscribe();
  }

  get currentContentLocale(): ContentLocale {
    return this.localizationService.currentContentLocale;
  }

  get originalContentLocale(): ContentLocale {
    return this.localizationService.originalContentLocale;
  }

  public onClick(): void {
    if (this.blockComponent?.selected) {
      this.toast.info('blocks.' + this.block.type + '.upload_deactivated');
    }
  }

  public onFile(file: File): void {
    if (!file) {
      return;
    }

    const blob = Blob.forUpload(file, this.slug);
    this.blobs.push(blob);
    this.subs.add(
      this.uploadService.upload(blob).subscribe(uploadedBlob => {
        // if blob is not in blobs, it was removed before upload finished, so we don't add it to data
        if (this.blobs?.indexOf(blob) === -1) {
          return;
        }

        this.addBlobToData(uploadedBlob);
      }, error => {
        // find blob in blobs and remove it with index of
        const index = this.blobs?.indexOf(blob);
        if (index > -1) {
          this.blobs.splice(index, 1);
        }
        this.toast.error(this.errorTranslationKey + '.errors.upload_error');
        Sentry.captureException(error);
      })
    );
  }

  public removeBlob(blob: Blob, event: Event): void {
    if (this.blobs.indexOf(blob) > -1) {
      this.blobs.splice(this.blobs.indexOf(blob), 1);
    }

    this.removeBlobFromData(blob);

    this.liveAnnouncer.announce(this.translocoService.translate('blocks.Block::Input::FileUploadInput.file_deleted'), 'assertive');

    event.preventDefault();
    event.cancelBubble = true;
  }

  private addBlobToData(blob: Blob) {
    if (!this.data) {
      this.data = [];
    }

    this.data.push(blob.signed_id);
    this.setData();
  }

  private removeBlobFromData(blob: Blob) {
    const existingSignedId = this.data?.find(signedId => {
      const naiveEquality = blob.signed_id === signedId;
      const sameEncodedId = blob.idFromSignedId === blobIdFromSignedId(signedId);
      return naiveEquality || sameEncodedId;
    });

    if (existingSignedId) {
      this.data.splice(this.data.indexOf(existingSignedId), 1);
      this.setData();
    }
  }

  private setData() {
    this.dataChange.emit(this.data);
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
    this.formControl.setValue(this.data, {emitViewToModelChange: false});
    this.cdr.markForCheck();
  }

  private _valid(): ValidationErrors | null {
    const required = !this.block.settings.required || this.data?.length > 0 ? null : {required: true};
    const uploading = !this.blobs?.some(blob => blob?.uploadPercentage > 0 && blob?.uploadPercentage < 100) ? null : {uploading: true};
    const errors = {...required, ...uploading};

    return Object.keys(errors).length > 0 ? errors : null;
  }
}
